import { Tooltip } from "@component/tooltip";
import LinkedJobCard from "@component/linked-job-card";
import { Job, JobSortState } from "@type/job";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BriefcaseIcon, InfoIcon, Loader2Icon, SearchIcon } from "lucide-react";
import { Input } from "@component/input";
import SortPanel from "@component/job-sort-filter";
import { JobStatusFilter as JobStatusFilterType } from "@type/job";
import JobStatusFilter from "@component/job-status-filter";
import { MetaPagination } from "@type/common";
import { defaultPaginationJob } from "@constant/app";
import { getJobListing } from "@service/job";
import { Button } from "@component/button";

const DEFAULT_PAGINATION = {
    page: defaultPaginationJob.page,
    limit: defaultPaginationJob.limit,
    total: 0,
    total_pages: 0
}

interface Props {
    assessment_id: string;
}

export default function JobLinkedAssessmentSection(props: Props) {
    const [searchParams] = useSearchParams();
    const hasMountedRef = useRef(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [keywords, setKeywords] = useState<string>("");
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [, setIsLoadingJobs] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [paginationMetadata, setPaginationMetadata] = useState<MetaPagination>(DEFAULT_PAGINATION)
    const [linkedJobs, setLinkedJobs] = useState<Job[]>([]);
    const [filters, setFilters] = useState<JobStatusFilterType>({
        status: [],
        job_type: [],
    });
    const [sortOptions, setSortOptions] = useState<JobSortState>({
        job_date: "",
        job_title: "",
    });

    const fetchJobs = async (keyword: string, currentPage: number, currentStatus: string[], sortOptions: JobSortState | undefined, isLoadMore: boolean) => {
        try {
            if (props.assessment_id === "") return;
            if (isLoadMore) {
                setIsLoadingMore(true);
            } else {
                setIsLoadingJobs(true);
            }

            let sortBy: string[] = [];
            let sort: string[] = [];
            if (sortOptions?.job_title) {
                sortBy = [...sortBy, "job_title"];
                sort = [...sort, sortOptions.job_title];
            }
            if (sortOptions?.job_date) {
                sortBy = [...sortBy, "job_date"];
                sort = [...sort, sortOptions.job_date];
            }

            const resp = await getJobListing({
                keyword: keyword,
                page: currentPage,
                limit: defaultPaginationJob.limit,
                job_status: currentStatus.filter((status) => (status !== "Public" && status !== "Private")).join(",") || "",
                job_visibility: currentStatus.filter((status) => (status === "Public" || status === "Private")).join(",") || "",
                sort_by: sortBy.join(",") || "",
                sort: sort.join(",") || "",
                assessment_id: props.assessment_id
            })

            if (resp.status === 200) {
                if (isLoadMore) {
                    setLinkedJobs((prevJobs: Job[]) => {
                        return [...prevJobs, ...resp.data.data];
                    });
                } else {
                    setLinkedJobs(resp.data.data);
                }
            }

            setPaginationMetadata(resp?.data?.meta?.pagination || DEFAULT_PAGINATION);
        } catch (err) {
            console.error("Failed to fetch jobs:", err);
            setPaginationMetadata(DEFAULT_PAGINATION);
        } finally {
            if (isLoadMore) {
                setIsLoadingMore(false);
            } else {
                setIsLoadingJobs(false);
            }
            setIsSearching(false);
        }
    };

    useEffect(() => {
        setIsLoadingJobs(true);
        const savedSortOptions = localStorage.getItem("jobLinkedSortOption") ? JSON.parse(localStorage.getItem("jobLinkedSortOption") || '{}') : { job_date: "", job_title: "" };
        const savedFilters = localStorage.getItem("jobFilters") ? JSON.parse(localStorage.getItem("jobFilters") || '{}') : {
            status: ["Published", "Paused", "Closed"]
        };
        setFilters(savedFilters);
        setSortOptions(savedSortOptions);
        fetchJobs("", defaultPaginationJob.page, savedFilters.status, savedSortOptions, false).then(() => {
            setIsLoadingJobs(false);
        });
        hasMountedRef.current = true;
    }, [])

    // Re-fetch jobs when navigating to the jobs tab via query parameter
    useEffect(() => {
        if (!hasMountedRef.current) return;
        if (searchParams.get("tab") === "jobs" && props.assessment_id) {
            fetchJobs(keywords, defaultPaginationJob.page, filters.status, sortOptions, false);
        }
    }, [searchParams.get("tab"), props.assessment_id])

    const onChangeSearch = (value: string) => {
        setKeywords(value)

        if (hasSearched && value === "") {
            setHasSearched(false);
            fetchJobs("", defaultPaginationJob.page, filters.status, sortOptions, false)
        }
    }

    const handleOnFiltersChange = (newFilters: JobStatusFilterType) => {
        setFilters(newFilters);
        fetchJobs(keywords, defaultPaginationJob.page, newFilters.status, sortOptions, false)
    }

    const handleOnSortChange = (newSortOptions: JobSortState) => {
        setSortOptions(newSortOptions);
        fetchJobs(keywords, defaultPaginationJob.page, filters.status, newSortOptions, false)
    }

    const handleLoadMore = async () => {
        setCurrentPage((prev) => prev + 1);
        await fetchJobs(keywords, currentPage + 1, filters.status, sortOptions, true);
    };

    const handleSearchSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSearching(true);
        fetchJobs(keywords, defaultPaginationJob.page, filters.status, sortOptions, false)
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">
                        Linked Jobs ({linkedJobs.length})
                    </h2>
                    <Tooltip
                        side="right"
                        content={(
                            <p>
                                Job listings linked with this screening will be
                                displayed here.
                            </p>
                        )}
                    >
                        <div
                            className="flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-600 rounded-full cursor-help"
                            style={{ backgroundColor: "transparent" }}
                        >
                            <InfoIcon className="h-4 w-4" />
                        </div>
                    </Tooltip>
                </div>
            </div>

            {/* Search and Filter Section */}
            <div>
                <div className="flex flex-row flex-wrap gap-2">
                    <form onSubmit={handleSearchSubmit} className="relative flex-1">
                        <div className="relative flex-grow">
                            <SearchIcon
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                            />
                            <Input
                                className="pl-9 w-full"
                                placeholder="Try searching 'Software Engineer', 'Marketing Manager' etc."
                                value={keywords}
                                onChange={(e) => onChangeSearch(e.target.value)}
                            />
                            {isSearching && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    <Loader2Icon className="h-4 w-4 text-blue-600 animate-spin" />
                                </div>
                            )}

                            {hasSearched && !isSearching && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                                    displaying {paginationMetadata.total} results
                                </div>
                            )}
                        </div>
                        <button type="submit" className="hidden">Find</button>
                    </form>
                    <div className="flex sm:flex-row flex-col gap-2">
                        <JobStatusFilter filters={filters} onFiltersChange={handleOnFiltersChange} />
                        <SortPanel optionName="jobLinkedSortOption" sortOptions={sortOptions} onSortChange={handleOnSortChange} />
                    </div>
                </div>
            </div>

            {linkedJobs.length === 0 ? (
                <div
                    className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300"
                    style={{
                        backgroundColor: "#ffffff",
                        border: "0px dashed rgb(228, 228, 231)",
                    }}
                >
                    <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4">
                        <BriefcaseIcon className="h-8 w-8 text-gray-400" />
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        No jobs linked yet.
                    </h3>
                    <p className="text-sm font-normal text-gray-600">
                        This screening hasn't been linked to any job listings yet.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {linkedJobs.map((job, index) => (
                        <LinkedJobCard key={index} assessment_id={props.assessment_id} job={job} />
                    ))}
                </div>
            )}

            {(linkedJobs || []).length > 0 &&
                currentPage < paginationMetadata.total_pages && (
                    <div className="mt-6 flex justify-center">
                        <Button
                            variant="outline"
                            className="text-sm bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                            onClick={handleLoadMore}
                            disabled={isLoadingMore}
                        >
                            {isLoadingMore ? (
                                <div className="flex items-center gap-2">
                                    <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                                    Loading...
                                </div>
                            ) : (
                                "Load More"
                            )}
                        </Button>
                    </div>
                )}
        </div>
    )
}